import { FC } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HistoricVolumeBatchUploadResult, HistoricVolumeByWorkingUnitUploadResult } from '@callmart/types';

type UploadResult = HistoricVolumeByWorkingUnitUploadResult | HistoricVolumeBatchUploadResult;

const isBatchResult = (result: UploadResult): result is HistoricVolumeBatchUploadResult =>
    'results' in result && Array.isArray(result.results);

interface HistoricVolumeUploadResultDialogProps {
    open: boolean;
    result: UploadResult;
    onClose: () => void;
    onUploadAnother?: () => void;
}

const SingleResultContent: FC<{
    result: HistoricVolumeByWorkingUnitUploadResult;
    t: (key: string, opts?: any) => string;
}> = ({ result, t }) => (
    <Alert severity={result.unchanged ? 'info' : 'success'}>
        <Typography variant="body1">
            <span>
                {result.unchanged
                    ? t('upload.unchangedForUnit', {
                          workingUnit: result.workingUnit.name,
                      })
                    : t('upload.uploadedForUnit', {
                          workingUnit: result.workingUnit.name,
                      })}
            </span>
        </Typography>
        {!result.unchanged && (
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                {result.created > 0 && <div>{t('upload.addedNewRecords', { created: result.created })}</div>}
                {result.updated > 0 && <div>{t('upload.updatedRecords', { updated: result.updated })}</div>}
                {result.softDeleted > 0 && <div>{t('upload.removedRecords', { softDeleted: result.softDeleted })}</div>}
            </Typography>
        )}
    </Alert>
);

const BatchResultContent: FC<{ result: HistoricVolumeBatchUploadResult; t: (key: string, opts?: any) => string }> = ({
    result,
    t,
}) => {
    const changedResults = result.results.filter((r) => !r.unchanged);
    const unchangedResults = result.results.filter((r) => r.unchanged);

    return (
        <>
            <Alert severity={changedResults.length > 0 ? 'success' : 'info'}>
                <Typography variant="body1">
                    {t('import.processedWorkingUnits', { count: result.results.length })}
                </Typography>
            </Alert>

            {result.unmatchedWorkingUnits.length > 0 && (
                <Alert severity="warning">
                    <Box>
                        <Typography variant="body2" fontWeight="bold">
                            {t('upload.unmatchedUnitsLabel')}
                        </Typography>
                        {result.unmatchedWorkingUnits.map((wu) => (
                            <Typography key={wu.workingUnitName} variant="body2">
                                • {wu.workingUnitName} - {t('import.rowsAffected', { count: wu.rowCount })}
                            </Typography>
                        ))}
                    </Box>
                </Alert>
            )}

            {changedResults.length > 0 && (
                <Box>
                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {t('upload.updatedUnitsLabel')}
                    </Typography>
                    {changedResults.map((r) => {
                        const parts = [
                            r.created > 0 && t('import.added', { created: r.created }),
                            r.updated > 0 && t('import.updated', { updated: r.updated }),
                            r.softDeleted > 0 && t('import.removed', { softDeleted: r.softDeleted }),
                        ].filter(Boolean);
                        return (
                            <Typography key={r.workingUnit.id} variant="body2" component="div" sx={{ ml: 1, mb: 0.5 }}>
                                <strong>{r.workingUnit.name}</strong>
                                <span>{parts.length > 0 ? ` — ${parts.join(', ')}` : ''}</span>
                            </Typography>
                        );
                    })}
                </Box>
            )}

            {unchangedResults.length > 0 && (
                <Box>
                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                        {t('upload.unchangedUnitsLabel')}
                    </Typography>
                    {unchangedResults.map((r) => (
                        <Typography key={r.workingUnit.id} variant="body2" sx={{ ml: 1 }}>
                            {r.workingUnit.name}
                        </Typography>
                    ))}
                </Box>
            )}

            {result.unmatchedRows.length > 0 && (
                <Alert severity="warning">
                    <Box sx={{ mt: result.unmatchedWorkingUnits.length > 0 ? 1 : 0 }}>
                        <Typography variant="body2" fontWeight="bold">
                            {t('upload.otherErrorsLabel')}
                        </Typography>
                        {result.unmatchedRows.map((row, idx) => (
                            <Typography key={idx} variant="body2">
                                {t('import.rowError', {
                                    row: row.row,
                                    reason: row.workingUnitName
                                        ? `"${row.workingUnitName}" — ${row.reason}`
                                        : row.reason,
                                })}
                            </Typography>
                        ))}
                    </Box>
                </Alert>
            )}
        </>
    );
};

const HistoricVolumeUploadResultDialog: FC<HistoricVolumeUploadResultDialogProps> = ({
    open,
    result,
    onClose,
    onUploadAnother,
}) => {
    const { t } = useTranslation('forecasts');

    const hasWarnings = result.sheets.some((sheet) => sheet.warnings.length > 0);
    const hasErrors = result.sheets.some((sheet) => sheet.errors.length > 0);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('upload.completeTitle')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    {isBatchResult(result) ? (
                        <BatchResultContent result={result} t={t} />
                    ) : (
                        <SingleResultContent result={result} t={t} />
                    )}

                    {hasWarnings && (
                        <Alert severity="warning">
                            <Typography variant="body2" fontWeight="bold">
                                {t('upload.warningsLabel')}
                            </Typography>
                            {result.sheets.map((sheet) =>
                                sheet.warnings.map((warning, idx) => (
                                    <Typography key={`${sheet.name}-${idx}`} variant="body2">
                                        {sheet.name}: {warning.message}
                                    </Typography>
                                ))
                            )}
                        </Alert>
                    )}

                    {hasErrors && (
                        <Alert severity="error">
                            <Typography variant="body2" fontWeight="bold">
                                {t('upload.errorsLabel')}
                            </Typography>
                            {result.sheets.map((sheet) =>
                                sheet.errors.map((error, idx) => (
                                    <Typography key={`${sheet.name}-${idx}`} variant="body2">
                                        {sheet.name}: {error.message}
                                    </Typography>
                                ))
                            )}
                        </Alert>
                    )}

                    <Box>
                        <Typography variant="body2" color="textSecondary">
                            {t('upload.processedSheetsLabel')}
                        </Typography>
                        {result.sheets.map((sheet) => (
                            <Typography key={sheet.name} variant="body2">
                                • {sheet.name}: {sheet.rows} {t('field.rows')}, {sheet.measures.length}{' '}
                                {t('field.measures')} ({sheet.measures.join(', ')})
                            </Typography>
                        ))}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                {onUploadAnother && (
                    <Button onClick={onUploadAnother} variant="outlined">
                        {t('upload.another')}
                    </Button>
                )}
                <Button onClick={onClose} variant="contained">
                    {t('action.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HistoricVolumeUploadResultDialog;
